import { SliderModal, VStack } from '@genial/components';
import { Color, Spacing } from '@genial/design-tokens';
import { instanceOfErrorListResponse, type ErrorListResponse } from '@genial/services';
import { useBreakpointValue } from '@genial/utils';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { ScrollView } from 'native-base';
import React, { useCallback, useEffect } from 'react';
import { Dimensions } from 'react-native';

import { useRecoilQuery } from '../../hooks/useRecoilQuery';
import { useToastCreator } from '../../hooks/useToastCreator';
import { SignedInRootStackParamList } from '../../routes/types';
import { equityOrderBookBySymbolQuery } from '../../store/product/selectors';

import { BuySellContainer } from './components/BuySellContainer';
import { Footer } from './components/Footer';
import { Header } from './components/Header';
import { Offer } from './components/Offer';
import { Separator } from './components/Separator';

interface EquityOffersBookModalProps {}

const { height } = Dimensions.get('window');

export const EquityOffersBookModal: React.FC<EquityOffersBookModalProps> = () => {
  const navigation = useNavigation();
  const { showToastFromError } = useToastCreator('equity-offers-book-toast');
  const { params } = useRoute<RouteProp<SignedInRootStackParamList, 'EquityOffersBookModal'>>();
  const canUpdate = !!params.canUpdate;
  const shouldFetchOrderBook = !!(params.quote.symbol && canUpdate);

  const {
    data: orderBookResponse,
    loading: orderBookIsLoading,
    error: orderBookIsError,
    refresher: handleOrderBookRefresh,
  } = useRecoilQuery(
    equityOrderBookBySymbolQuery({
      symbol: params.quote.symbol!,
    }),
    { if: () => shouldFetchOrderBook }
  );

  const data = orderBookResponse?.data || params;

  const isDesktop = useBreakpointValue({ base: false, lg: true });
  const direction = isDesktop ? 'right' : 'bottom';

  const goBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
  }, [navigation]);

  useEffect(() => {
    if (orderBookIsError) {
      if (instanceOfErrorListResponse(orderBookIsError)) {
        (orderBookIsError as unknown as ErrorListResponse).errors?.forEach((errorItem) =>
          showToastFromError(errorItem)
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBookIsError]);

  return (
    <SliderModal dismissWhenClickOutside onDismiss={goBack} width={0.33} direction={direction}>
      <VStack maxHeight={{ base: height * 0.8, lg: 'full' }} flex={1}>
        <Header
          goBack={goBack}
          canUpdate={canUpdate}
          isLoading={orderBookIsLoading}
          onRefresh={handleOrderBookRefresh}
        />
        <ScrollView showsVerticalScrollIndicator={false} backgroundColor={Color['neutral.white']}>
          <VStack
            px={Spacing['inline.xxs']}
            pb={Spacing['inline.xxs']}
            space={Spacing['stack.xxxs']}>
            <Offer {...data} isLoading={orderBookIsLoading} />
            <Separator />
            <BuySellContainer {...data} isLoading={orderBookIsLoading} />
            <Separator />
            <Footer {...data} isLoading={orderBookIsLoading} />
          </VStack>
        </ScrollView>
      </VStack>
    </SliderModal>
  );
};
